import { useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import type { CharacterState, Rules } from "@/data/types";
import { getRulesForVersion } from "@/data/rules";
import { calcDerivedStats } from "@/lib/characterCalc";
import { encodeCharacter } from "@/lib/characterUrl";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ThemeToggle } from "@/components/theme-toggle";
import { CharacterSummary } from "./CharacterSummary";
import { ChevronLeft, ChevronRight, Wand2, Eye, Home } from "lucide-react";
import { STEPS, loadInitialState } from "./shared";
import { StepNameBackground } from "./StepNameBackground";
import { StepCoreSkills } from "./StepCoreSkills";
import { StepDarkOfferings } from "./StepDarkOfferings";
import { StepOtherworldlyGifts } from "./StepOtherworldlyGifts";
import { StepSpellAllocation } from "./StepSpellAllocation";
import { StepReviewShare } from "./StepReviewShare";
import { RulesUpgradeBanner } from "./RulesUpgrade";

export function CharacterCreator() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [state, setState] = useState<CharacterState>(() =>
    loadInitialState(searchParams)
  );
  const [showSheet, setShowSheet] = useState(() => !!searchParams.get("c"));
  const [copied, setCopied] = useState(false);
  const rules: Rules = getRulesForVersion(state.rulesVersion);

  // Update URL when state changes
  const updateUrl = useCallback((newState: CharacterState) => {
    const encoded = encodeCharacter(newState);
    const url = new URL(window.location.href);
    url.pathname = "/creator";
    url.searchParams.set("c", encoded);
    window.history.replaceState(null, "", url.toString());
  }, []);

  const update = useCallback(
    (newState: CharacterState) => {
      setState(newState);
      updateUrl(newState);
    },
    [updateUrl]
  );

  const stats = calcDerivedStats(state, rules);
  const _allPacts = [...state.pacts, ...(state.bonusPacts || [])];
  const maxPacts = 3 + state.bonusGifts;

  if (showSheet) {
    return (
      <div className="min-h-screen p-4">
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-3 items-center mb-6">
            <Button
              variant="ghost"
              className="justify-self-start"
              onClick={() => setShowSheet(false)}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Builder
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="justify-self-center"
              onClick={() => navigate("/")}
            >
              <Home className="h-4 w-4 mr-1" />
              Home
            </Button>
            <div className="justify-self-end">
              <ThemeToggle />
            </div>
          </div>
          <RulesUpgradeBanner state={state} onChange={update} />
          <CharacterSummary
            state={state}
            rules={rules}
            onEdit={() => setShowSheet(false)}
            onChange={update}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-3 items-center mb-6">
          <div className="flex items-center gap-2 justify-self-start">
            <Wand2 className="h-5 w-5 text-primary" />
            <span className="font-semibold">Character Creator</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="justify-self-center"
            onClick={() => navigate("/")}
          >
            <Home className="h-4 w-4 mr-1" />
            Home
          </Button>
          <div className="flex items-center gap-2 justify-self-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSheet(true)}
            >
              <Eye className="h-4 w-4 mr-1" />
              View Sheet
            </Button>
            <ThemeToggle />
          </div>
        </div>

        {step === STEPS.length - 1 && (
          <RulesUpgradeBanner state={state} onChange={update} />
        )}

        {/* Step indicator */}
        <div className="mb-6 w-full">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{STEPS[step]}</span>
            <span className="text-xs text-muted-foreground">
              Step {step + 1} of {STEPS.length}
            </span>
          </div>
          <Progress value={((step + 1) / STEPS.length) * 100} className="h-2" />
          <div className="flex mt-2 gap-1">
            {STEPS.map((s, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`flex-1 h-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`}
                title={s}
              />
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between mb-6 w-full">
          <Button
            variant="outline"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={() => setShowSheet(true)}>
              <Eye className="h-4 w-4 mr-1" />
              View Character Sheet
            </Button>
          )}
        </div>

        {/* Step content */}
        <div className="mb-6 w-full">
          {step === 0 && <StepNameBackground state={state} onChange={update} />}
          {step === 1 && (
            <StepCoreSkills
              state={state}
              rules={rules}
              stats={stats}
              onChange={update}
            />
          )}
          {step === 2 && (
            <StepDarkOfferings
              state={state}
              rules={rules}
              stats={stats}
              onChange={update}
              maxPacts={maxPacts}
            />
          )}
          {step === 3 && (
            <StepOtherworldlyGifts
              state={state}
              rules={rules}
              stats={stats}
              onChange={update}
            />
          )}
          {step === 4 && (
            <StepSpellAllocation
              state={state}
              rules={rules}
              stats={stats}
              onChange={update}
            />
          )}
          {step === 5 && (
            <StepReviewShare
              state={state}
              rules={rules}
              stats={stats}
              onChange={update}
              copied={copied}
              setCopied={setCopied}
              onViewSheet={() => setShowSheet(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
